<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import {
  UiChip,
  UiCountryFlag,
  UiDivider,
  UiIcon,
  UiInfoBox,
  UiProse,
  UiText,
  UiTooltip,
  countriesShopify,
  languagesEn,
  timezonesEn,
  useApi,
  type BuilderRowConfig,
} from '@shopexperts/shared';
import type { AdminExpertDetail } from '@shopexperts/legacy-api';
import { shopifyRoleOptions, agencyRepresentativeRoleOptions } from '@/utils/roles';
import { useGeoRegions } from '@/pages/expert/listing/composables/useGeoRegions';
import { useExpertDetailsUpdate } from '@/app/composables/expert-manager/useExpertDetailsUpdate';
import CityCombobox from '@/app/components/location/CityCombobox.vue';
import ExpertDetailsSection from './ExpertDetailsSection.vue';
import ExpertFlagsSection from './ExpertFlagsSection.vue';
import ExpertDetailsFact from './ExpertDetailsFact.vue';
import {
  buildDetailsPayload,
  hasLinkedUser,
  mapDetailsErrors,
  sectionFields,
  sectionInitialValues,
  sectionSchema,
  type DetailsFieldDef,
  type DetailsFieldValue,
  type DetailsSectionKey,
} from './expertDetailsForm';

const props = defineProps<{
  expert: AdminExpertDetail;
}>();

const SECTIONS: DetailsSectionKey[] = ['listing', 'services', 'reach', 'account', 'contact', 'links'];

const COUNTRY_OPTIONS = countriesShopify.map((country) => ({ label: country.name, value: country.iso2 }));
const TIMEZONE_OPTIONS = timezonesEn.map((timezone) => ({
  label: `${timezone.iana.replace(/_/g, ' ')} (${timezone.offset})`,
  value: timezone.iana,
}));
const LANGUAGE_OPTIONS = languagesEn.map((language) => ({ label: language.name, value: language.code }));
const COUNTRY_NAMES = new Map(countriesShopify.map((country) => [country.iso2, country.name]));
const LANGUAGE_NAMES = new Map(languagesEn.map((language) => [language.code, language.name]));

const { options: geoRegionOptions } = useGeoRegions();

const api = useApi();
const { data: serviceCatalog } = useQuery({
  queryKey: ['service-categories', 'with-subs'],
  queryFn: () => api.categories.getCategoriesWithSubcategories(),
  staleTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
});
const subcategoryOptions = computed(() =>
  (serviceCatalog.value ?? []).flatMap((category) =>
    category.subcategories.map((subcategory) => ({ label: `${category.name}: ${subcategory.name}`, value: String(subcategory.id) })),
  ),
);
const subcategoryOptionGroups = computed(() =>
  (serviceCatalog.value ?? []).map((category) => ({
    id: `category-${category.id}`,
    label: category.name,
    items: category.subcategories.map((subcategory) => ({ label: subcategory.name, value: String(subcategory.id) })),
  })),
);
const geoRegionLabels = computed(() => new Map(geoRegionOptions.value.map((option) => [option.value, option.label])));

const isAgency = computed(() => props.expert.expert_type === 'agency');
const isAppServiceProvider = computed(() => props.expert.listing_type === 'app_service_provider');

// ---- Editing state: one section at a time ------------------------------------------------

const activeSection = ref<DetailsSectionKey | null>(null);
/** Values when the section opened. The diff runs against these, not against an expert that may have refetched meanwhile. */
const openedWith = ref<Record<string, DetailsFieldValue>>({});
const serverErrors = ref<Record<string, string | undefined>>({});
const formError = ref<string | null>(null);

const { updateDetails } = useExpertDetailsUpdate();
const saving = computed(() => updateDetails.isPending.value);

const fieldsBySection = computed(() =>
  Object.fromEntries(SECTIONS.map((section) => [section, sectionFields(section, props.expert)])) as Record<DetailsSectionKey, DetailsFieldDef[]>,
);

function setEditing(section: DetailsSectionKey, editing: boolean) {
  if (editing) {
    openedWith.value = sectionInitialValues(fieldsBySection.value[section], props.expert);
    serverErrors.value = {};
    formError.value = null;
    activeSection.value = section;
  } else if (activeSection.value === section) {
    activeSection.value = null;
  }
}

function sectionProps(section: DetailsSectionKey) {
  const editing = activeSection.value === section;
  return {
    rows: rows.value[section],
    schema: section === 'services' ? servicesSchema.value : sectionSchema(fieldsBySection.value[section]),
    initialValues: editing ? openedWith.value : undefined,
    editing,
    locked: activeSection.value !== null && !editing,
    saving: editing && saving.value,
    serverErrors: editing ? serverErrors.value : undefined,
    formError: editing ? formError.value : null,
  };
}

function clearServerErrors() {
  if (Object.keys(serverErrors.value).length) serverErrors.value = {};
  formError.value = null;
}

async function save(section: DetailsSectionKey, values: Record<string, unknown>) {
  const fields = fieldsBySection.value[section];
  const payload = buildDetailsPayload(fields, openedWith.value, values);
  if (!payload) {
    activeSection.value = null;
    return;
  }

  serverErrors.value = {};
  formError.value = null;
  try {
    await updateDetails.mutateAsync({ expertId: props.expert.id, body: payload });
    activeSection.value = null;
  } catch (error) {
    const mapped = mapDetailsErrors(fields, error);
    serverErrors.value = mapped.fieldErrors;
    formError.value = mapped.message;
  }
}

const sectionRefs = ref<Partial<Record<DetailsSectionKey, InstanceType<typeof ExpertDetailsSection>>>>({});

function sectionRef(section: DetailsSectionKey) {
  return (el: unknown) => {
    sectionRefs.value[section] = (el as InstanceType<typeof ExpertDetailsSection> | null) ?? undefined;
  };
}

defineExpose({
  openSection: async (section: DetailsSectionKey) => {
    // Same rule as the disabled pencils: never replace a section that is already being
    // edited, its draft would be lost. Bring the admin to the open one instead.
    const target = activeSection.value ?? section;
    if (!activeSection.value) setEditing(section, true);
    // Wait for the form to render and lay out, then jump instantly: a smooth scroll gets
    // cancelled by the layout shifts of a freshly mounted tab.
    await nextTick();
    await new Promise(requestAnimationFrame);
    sectionRefs.value[target]?.$el?.scrollIntoView?.({ block: 'start' });
  },
});

// ---- Form layouts --------------------------------------------------------------------------

function onlyAvailable(section: DetailsSectionKey, layout: BuilderRowConfig[]): BuilderRowConfig[] {
  const available = new Set(fieldsBySection.value[section].map((field) => field.name));
  return layout
    .map((row) => ({ columns: row.columns.filter((column) => 'heading' in column || available.has(column.name)) }))
    .filter((row) => row.columns.length > 0);
}

function urlField(name: string, label: string, hint?: string): BuilderRowConfig {
  return { columns: [{ type: 'url', name, label, hint, props: { placeholder: 'https://' } }] };
}

const listingLayout = computed<BuilderRowConfig[]>(() => {
  const nameRows: BuilderRowConfig[] = isAgency.value
    ? [
        {
          columns: [
            {
              type: 'text',
              name: 'agencyName',
              label: isAppServiceProvider.value ? 'App business name' : 'Agency name',
            },
            { type: 'text', name: 'startingPrice', label: 'Starting price (USD)', props: { placeholder: '450' } },
          ],
        },
        {
          columns: [
            { type: 'text', name: 'representativeName', label: 'Representative name', props: { placeholder: 'Full name' } },
            {
              type: 'select',
              name: 'representativeRole',
              label: 'Representative role',
              props: { placeholder: 'Select a role', options: agencyRepresentativeRoleOptions },
            },
          ],
        },
      ]
    : [
        {
          columns: [
            { type: 'text', name: 'firstName', label: 'First name' },
            { type: 'text', name: 'lastName', label: 'Last name' },
          ],
        },
        {
          columns: [
            { type: 'select', name: 'role', label: 'Role', props: { placeholder: 'Select a role', options: shopifyRoleOptions } },
            { type: 'text', name: 'startingPrice', label: 'Starting price (USD)', props: { placeholder: '450' } },
          ],
        },
      ];

  return [
    ...nameRows,
    { columns: [{ type: 'richtext', name: 'bio', label: 'Bio', props: { placeholder: 'Short bio shown on the public profile' } }] },
  ];
});

const servicesDescription = computed(() => {
  const bare = props.expert.offered_services
    .filter((service) => !service.sub_categories.length)
    .map((service) => service.service_category.name);
  const base = 'The services the directory filters and category pages match this expert for.';
  return bare.length
    ? `${base} Saving drops ${bare.join(', ')}: a category stays listed only through a picked service.`
    : base;
});

const servicesDraft = ref<Record<string, unknown>>();

function onServicesInput(values: Record<string, unknown> | undefined) {
  servicesDraft.value = values;
  clearServerErrors();
}

const planSubcategoryLimit = computed(() => props.expert.subcategory_plan_limit);

const subcategoryLimit = computed(() => {
  const draft = activeSection.value === 'services' ? servicesDraft.value?.subcategoryLimitOverride : undefined;
  if (typeof draft === 'string') {
    const trimmed = draft.trim();
    if (trimmed === '') return planSubcategoryLimit.value;
    const value = Number(trimmed);
    if (Number.isInteger(value) && value >= 1 && value <= 100) return value;
  }
  return props.expert.subcategory_limit;
});

const pickedSubcategoryCount = computed(() =>
  new Set(props.expert.offered_services.flatMap((service) => service.sub_categories.map((sub) => sub.id))).size,
);

const servicesSchema = computed(() =>
  sectionSchema(fieldsBySection.value.services).superRefine((values, ctx) => {
    const limit = subcategoryLimit.value;
    const picked = Array.isArray(values.subcategories) ? values.subcategories.length : 0;
    if (limit !== undefined && picked > limit) {
      ctx.addIssue({ code: 'custom', path: ['subcategories'], message: `This listing can have up to ${limit} subcategories.` });
    }
  }),
);

const servicesLayout = computed<BuilderRowConfig[]>(() => [
  {
    columns: [
      {
        type: 'text',
        name: 'subcategoryLimitOverride',
        label: 'Subcategory limit override',
        hint: planSubcategoryLimit.value === undefined
          ? 'Leave empty to use the plan limit.'
          : `Leave empty to use the plan limit (${planSubcategoryLimit.value}).`,
        props: { placeholder: 'Plan limit' },
      },
    ],
  },
  {
    columns: [
      {
        type: 'multiselect-chips',
        name: 'subcategories',
        label: 'Services',
        inlineHint: subcategoryLimit.value === undefined ? undefined : `Up to ${subcategoryLimit.value}`,
        hint: 'Categories appear on the profile in the order their first service is picked.',
        props: {
          placeholder: 'Type to add a service',
          options: subcategoryOptions.value,
          optionGroups: subcategoryOptionGroups.value,
          max: subcategoryLimit.value,
          openOnClick: true,
          showChevron: true,
        },
      },
    ],
  },
]);

const reachDraft = ref<Record<string, unknown>>();
const draftCountry = computed(() => {
  const value = activeSection.value === 'reach' ? reachDraft.value?.country : props.expert.country;
  return typeof value === 'string' ? value : '';
});
const storedCity = computed(() => (draftCountry.value === props.expert.country ? props.expert.city ?? null : null));

function onReachInput(values: Record<string, unknown> | undefined) {
  reachDraft.value = values;
  clearServerErrors();
}

watch(draftCountry, (next, prev) => {
  if (activeSection.value !== 'reach' || !prev || next === prev || !reachDraft.value?.cityId) return;
  const restoresInitial = next === openedWith.value.country && reachDraft.value.cityId === openedWith.value.cityId;
  if (!restoresInitial) sectionRefs.value.reach?.setValue('cityId', '');
});

const reachLayout = computed<BuilderRowConfig[]>(() => [
  {
    columns: [
      { type: 'combobox', name: 'country', label: 'Country', props: { placeholder: 'Search countries', options: COUNTRY_OPTIONS } },
      {
        type: 'custom',
        name: 'cityId',
        label: 'City',
        component: CityCombobox,
        props: { country: draftCountry.value, initialCity: storedCity.value },
      },
    ],
  },
  {
    columns: [
      { type: 'combobox', name: 'timezone', label: 'Timezone', props: { placeholder: 'Search timezones', options: TIMEZONE_OPTIONS } },
    ],
  },
  {
    columns: [
      {
        type: 'multiselect-chips',
        name: 'languages',
        label: 'Languages',
        props: { placeholder: 'Type to add a language', options: LANGUAGE_OPTIONS, openOnClick: true, showChevron: true },
      },
    ],
  },
  {
    columns: [
      {
        type: 'multiselect-chips',
        name: 'geoRegions',
        label: 'Regions served',
        inlineHint: 'Not limited by the expert’s plan',
        props: { placeholder: 'Type to add a region', options: geoRegionOptions.value, openOnClick: true, showChevron: true },
      },
    ],
  },
]);

const ACCOUNT_LAYOUT: BuilderRowConfig[] = [
  { columns: [{ type: 'text', name: 'accountFirstName', label: 'Account first name' }] },
  { columns: [{ type: 'text', name: 'accountLastName', label: 'Account last name' }] },
  {
    columns: [
      {
        type: 'email',
        name: 'accountEmail',
        label: 'Login email',
        hint: 'Changing it cancels any email change the expert has pending.',
        props: { type: 'email' },
      },
    ],
  },
];

const CONTACT_LAYOUT: BuilderRowConfig[] = [
  { columns: [{ type: 'text', name: 'phone', label: 'Phone', inlineHint: 'With country code', props: { placeholder: '+14165550100' } }] },
  {
    columns: [
      {
        type: 'email',
        name: 'contactEmail',
        label: 'Contact email',
        hint: 'Shown as the listing contact on unclaimed profiles.',
        props: { type: 'email', placeholder: 'hello@example.com' },
      },
    ],
  },
];

const linksLayout = computed<BuilderRowConfig[]>(() => [
  urlField('website', 'Website'),
  urlField('shopifyDirectoryLink', isAppServiceProvider.value ? 'App Store listing' : 'Partner Directory'),
  urlField('linkedin', 'LinkedIn'),
  urlField('github', 'GitHub'),
  urlField('x', 'X'),
  urlField('fiverr', 'Fiverr'),
  urlField('upwork', 'Upwork'),
  urlField('toptal', 'Toptal'),
  urlField('storetasker', 'Storetasker'),
  urlField('otherListing', 'Other listing'),
  urlField('googleMerchant', 'Google Merchant listing'),
  urlField('bookingCallUrl', 'Booking call', 'Full URL including https://. Powers the free consultation button on Pro profiles.'),
]);

const rows = computed<Record<DetailsSectionKey, BuilderRowConfig[]>>(() => ({
  listing: onlyAvailable('listing', listingLayout.value),
  services: serviceCatalog.value ? onlyAvailable('services', servicesLayout.value) : [],
  reach: onlyAvailable('reach', reachLayout.value),
  account: onlyAvailable('account', ACCOUNT_LAYOUT),
  contact: onlyAvailable('contact', CONTACT_LAYOUT),
  links: onlyAvailable('links', linksLayout.value),
}));

// ---- Read views ----------------------------------------------------------------------------

const representativeLine = computed(() =>
  [props.expert.agency?.representative_name, props.expert.agency?.representative_role].filter(Boolean).join(' · '),
);
const startingPrice = computed(() =>
  props.expert.starting_price != null ? `From $${props.expert.starting_price.toLocaleString()}` : '',
);
const countryName = computed(() =>
  props.expert.country ? COUNTRY_NAMES.get(props.expert.country) ?? props.expert.country : '',
);
const cityLabel = computed(() => [props.expert.city?.name, props.expert.city?.region].filter(Boolean).join(', '));
const languageNames = computed(() =>
  (props.expert.languages ?? []).map((code) => LANGUAGE_NAMES.get(code) ?? code),
);
const regionLabels = computed(() =>
  props.expert.geo_regions.length
    ? props.expert.geo_regions.map((slug) => geoRegionLabels.value.get(slug) ?? slug)
    : props.expert.regions_served ?? [],
);
const accountName = computed(() =>
  [props.expert.user?.first_name, props.expert.user?.last_name].filter(Boolean).join(' '),
);
const linkedUser = computed(() => hasLinkedUser(props.expert));
const contactEmailKnown = computed(() => props.expert.social_links?.contact_email !== undefined);

type SocialLinkKey = keyof NonNullable<AdminExpertDetail['social_links']>;

interface ExpertLink {
  key: SocialLinkKey;
  label: string;
  icon: string;
  url: string;
}

/** Every link the expert can set, in the same order as the expert's own listing editor. */
const LINK_PLATFORMS: Omit<ExpertLink, 'url'>[] = [
  { key: 'website', label: 'Website', icon: 'globe-01' },
  { key: 'shopify_directory_link', label: 'Partner Directory', icon: 'shopify-directory' },
  { key: 'linkedin_url', label: 'LinkedIn', icon: 'linkedin' },
  { key: 'github_url', label: 'GitHub', icon: 'github' },
  { key: 'x_url', label: 'X', icon: 'twitter' },
  { key: 'fiverr_url', label: 'Fiverr', icon: 'fiverr' },
  { key: 'upwork_url', label: 'Upwork', icon: 'upwork' },
  { key: 'toptal_url', label: 'Toptal', icon: 'toptal' },
  { key: 'storetasker_url', label: 'Storetasker', icon: 'storetasker' },
  { key: 'other_listing_url', label: 'Other listing', icon: 'link-external' },
  { key: 'booking_call_url', label: 'Booking call', icon: 'calendar' },
  { key: 'google_merchant', label: 'Google Merchant listing', icon: 'map-pin' },
];

const profileLinks = computed<ExpertLink[]>(() => {
  const links = props.expert.social_links;
  if (!links) return [];
  return LINK_PLATFORMS
    .map((platform) => ({
      ...platform,
      label: platform.key === 'shopify_directory_link' && isAppServiceProvider.value ? 'App Store listing' : platform.label,
      url: links[platform.key]?.trim() ?? '',
    }))
    .filter((platform) => !!platform.url);
});

function displayUrl(url: string) {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}
</script>

<template>
  <div>
    <!-- Reviews -->
    <div v-if="expert.reviews" class="mb-8">
      <div class="flex items-center gap-5 mb-5">
        <div class="flex items-center gap-2">
          <UiIcon name="star" class="w-5 h-5 text-emerald-500 fill-emerald-500" />
          <span class="text-3xl font-bold text-gray-900 tabular-nums tracking-tight leading-none">{{ expert.reviews.overall.rating.toFixed(1) }}</span>
        </div>
        <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 text-xs font-medium text-gray-500">
          {{ expert.reviews.overall.reviews_count }}
          <span class="text-gray-400">{{ expert.reviews.overall.reviews_count === 1 ? 'review' : 'reviews' }}</span>
        </span>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-3">
        <div
          v-for="metric in [
            { label: 'Quality', value: expert.reviews.breakdown.quality_of_work },
            { label: 'Communication', value: expert.reviews.breakdown.communication },
            { label: 'Timelines', value: expert.reviews.breakdown.respects_timelines },
            { label: 'Value', value: expert.reviews.breakdown.value_for_money },
          ]"
          :key="metric.label"
          class="flex flex-col gap-1.5"
        >
          <span class="text-xs text-gray-400 uppercase tracking-wide">{{ metric.label }}</span>
          <div class="flex items-center gap-2">
            <div class="flex-1 h-1.5 rounded-full bg-emerald-500/10 overflow-hidden">
              <div class="h-full rounded-full bg-emerald-500 transition-all duration-500" :style="{ width: `${(metric.value / 5) * 100}%` }" />
            </div>
            <span class="text-xs font-semibold text-gray-700 tabular-nums w-6 text-right">{{ metric.value.toFixed(1) }}</span>
          </div>
        </div>
      </div>
    </div>

    <UiDivider v-if="expert.reviews" color="light" class="mb-7" />

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-x-10 gap-y-8">
      <!-- Main column -->
      <div class="lg:col-span-2 space-y-8">
        <ExpertDetailsSection
          title="Listing"
          description="What visitors see at the top of the public profile."
          :ref="sectionRef('listing')"
          v-bind="sectionProps('listing')"
          @update:editing="setEditing('listing', $event)"
          @save="save('listing', $event)"
          @input="clearServerErrors"
        >
          <dl class="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            <ExpertDetailsFact :label="isAgency ? (isAppServiceProvider ? 'App business name' : 'Agency name') : 'Name'" :value="expert.display_name" />
            <ExpertDetailsFact v-if="!isAgency && expert.freelancer !== undefined" label="Role" :value="expert.freelancer?.role" />
            <ExpertDetailsFact v-if="isAgency && expert.agency !== undefined" label="Representative" :value="representativeLine" />
            <ExpertDetailsFact label="Starting price" :value="startingPrice" />
            <ExpertDetailsFact label="Bio" wide>
              <UiProse v-if="expert.bio" :html="expert.bio" class="prose-sm max-w-none text-gray-600" />
              <UiText v-else as="span" variant="p-small" class="text-gray-300" text="—" />
            </ExpertDetailsFact>
          </dl>
        </ExpertDetailsSection>

        <UiDivider color="light" />

        <ExpertDetailsSection
          title="Offered services"
          :description="servicesDescription"
          :ref="sectionRef('services')"
          v-bind="sectionProps('services')"
          @update:editing="setEditing('services', $event)"
          @save="save('services', $event)"
          @input="onServicesInput"
        >
          <template v-if="expert.subcategory_limit !== undefined" #meta>
            <UiTooltip
              :content="expert.subcategory_limit_override == null
                ? 'Subcategory limit from the expert’s plan'
                : 'Subcategory limit set by an admin, replacing the plan limit'"
            >
              <UiChip
                :text="`${pickedSubcategoryCount} of ${expert.subcategory_limit}${expert.subcategory_limit_override == null ? '' : ' · override'}`"
                variant="default"
                size="xs"
                rounded="small"
              />
            </UiTooltip>
          </template>
          <div v-if="expert.offered_services?.length" class="space-y-4">
            <div
              v-for="(service, idx) in expert.offered_services"
              :key="service.id ?? `${service.service_category.id}-${idx}`"
            >
              <UiText as="span" variant="p-small" class="font-semibold text-gray-800">
                {{ service.service_category.name }}
              </UiText>
              <div v-if="service.sub_categories.length" class="flex flex-wrap gap-1.5 mt-2">
                <UiChip
                  v-for="sub in service.sub_categories"
                  :key="sub.id"
                  :text="sub.name"
                  variant="default"
                  size="xs"
                  rounded="small"
                />
              </div>
              <UiDivider v-if="idx < expert.offered_services.length - 1" color="light" class="mt-4" />
            </div>
          </div>
          <UiText v-else as="p" variant="p-small" class="text-gray-400" text="No services selected yet." />
        </ExpertDetailsSection>

        <UiDivider color="light" />

        <ExpertDetailsSection
          title="Location & reach"
          description="Where the expert is based and which languages and regions the directory filters match them for."
          :ref="sectionRef('reach')"
          v-bind="sectionProps('reach')"
          @update:editing="setEditing('reach', $event)"
          @save="save('reach', $event)"
          @input="onReachInput"
        >
          <dl class="grid grid-cols-2 gap-x-6 gap-y-4">
            <ExpertDetailsFact label="Country" :value="countryName">
              <div v-if="expert.country" class="flex items-center gap-2 min-w-0">
                <UiCountryFlag :country-code="expert.country" class="w-4 shrink-0 rounded-xs" />
                <UiText as="span" variant="p-small" class="text-gray-700 truncate" :text="countryName" />
              </div>
              <UiText v-else as="span" variant="p-small" class="text-gray-300" text="—" />
            </ExpertDetailsFact>
            <ExpertDetailsFact label="City" :value="cityLabel" />
            <ExpertDetailsFact label="Timezone" :value="expert.timezone" />
            <ExpertDetailsFact label="Languages">
              <div v-if="languageNames.length" class="flex flex-wrap gap-1.5">
                <UiChip v-for="name in languageNames" :key="name" :text="name" variant="default" size="xs" rounded="small" />
              </div>
              <UiText v-else as="span" variant="p-small" class="text-gray-300" text="—" />
            </ExpertDetailsFact>
            <ExpertDetailsFact label="Regions served">
              <div v-if="regionLabels.length" class="flex flex-wrap gap-1.5">
                <UiChip v-for="label in regionLabels" :key="label" :text="label" variant="default" size="xs" rounded="small" />
              </div>
              <UiText v-else as="span" variant="p-small" class="text-gray-300" text="—" />
            </ExpertDetailsFact>
          </dl>
        </ExpertDetailsSection>
      </div>

      <!-- Sidebar -->
      <div class="space-y-8">
        <ExpertDetailsSection
          title="Account"
          description="The login the expert signs in with. It is separate from the public listing."
          :ref="sectionRef('account')"
          v-bind="sectionProps('account')"
          @update:editing="setEditing('account', $event)"
          @save="save('account', $event)"
          @input="clearServerErrors"
        >
          <UiInfoBox v-if="!linkedUser" variant="info">
            <UiText as="span" variant="p-small">
              No login account is linked to this profile. Send an invite from the actions menu so the expert can claim it.
            </UiText>
          </UiInfoBox>
          <dl v-else class="grid grid-cols-1 gap-y-4">
            <ExpertDetailsFact v-if="expert.user !== undefined" label="Account holder" :value="accountName" />
            <ExpertDetailsFact label="Login email" :value="expert.user?.email ?? expert.email" />
          </dl>
        </ExpertDetailsSection>

        <UiDivider color="light" />

        <ExpertDetailsSection
          title="Contact"
          :ref="sectionRef('contact')"
          v-bind="sectionProps('contact')"
          @update:editing="setEditing('contact', $event)"
          @save="save('contact', $event)"
          @input="clearServerErrors"
        >
          <dl class="grid grid-cols-1 gap-y-4">
            <ExpertDetailsFact label="Phone" :value="expert.phone_no" />
            <ExpertDetailsFact v-if="contactEmailKnown" label="Contact email" :value="expert.social_links?.contact_email" />
          </dl>
        </ExpertDetailsSection>

        <UiDivider color="light" />

        <ExpertDetailsSection
          title="Links"
          :ref="sectionRef('links')"
          v-bind="sectionProps('links')"
          @update:editing="setEditing('links', $event)"
          @save="save('links', $event)"
          @input="clearServerErrors"
        >
          <div v-if="profileLinks.length" class="space-y-3">
            <div v-for="link in profileLinks" :key="link.key" class="flex items-start gap-2.5">
              <UiIcon :name="(link.icon as any)" class="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
              <div class="min-w-0 flex-1">
                <UiText as="span" variant="caption" class="text-gray-400 block">{{ link.label }}</UiText>
                <a
                  :href="link.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-baseline gap-1 text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                >
                  <UiText as="span" variant="p-small" class="break-all">{{ displayUrl(link.url) }}</UiText>
                  <UiIcon name="link-external" class="w-3 h-3 shrink-0 self-center opacity-50" />
                </a>
              </div>
            </div>
          </div>
          <UiText v-else as="p" variant="p-small" class="text-gray-400" text="No links added yet." />
        </ExpertDetailsSection>
      </div>
    </div>

    <UiDivider color="light" class="my-8" />

    <ExpertFlagsSection :expert="expert" />
  </div>
</template>
